import React, { useState } from 'react';
import { IconNode, Icon } from '../Global/DataUtils/iconQueries';
import { SortedIcons, iconSorter } from '../Global/DataUtils/iconSorter';
//@ts-ignore
import * as styles from './page-relevancy-tags.module.css';
import ArrowLgButton from '../Global/ArrowLgButton';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { PageRelevancyTagsProps } from './index';
import {
    TaxonomyObject,
    PageRelevancyCategory,
} from '../../templates/TaxonomyContext';
import { LazyMotion, domAnimation, m, AnimatePresence } from 'framer-motion';

interface Props extends PageRelevancyTagsProps {
    icons: IconNode[];
    taxonomies: TaxonomyObject;
}

const PageRelevancyTags: React.FC<Props> = ({
    statement,
    color,
    icons,
    taxonomies,
    kontentItemId,
}) => {
    const breakpoints = useBreakpoint();
    const iconColor = color.value[0].codename || 'active_orange';
    const sortedIcons = iconSorter(icons) as SortedIcons;

    let arrowStrokeColor: string;
    switch (iconColor) {
        case 'dark_gray':
            arrowStrokeColor = '#505251';
            break;
        case 'green':
            arrowStrokeColor = '#506642';
            break;
        case 'gray_green':
            arrowStrokeColor = '#4b5f5e';
            break;
        default:
            arrowStrokeColor = '#f06623';
    }

    const relevantPageTaxonomy =
        taxonomies?.for_page_relevancy?.value[0]?.elements
            .associated_taxonomy_code_name.value || '';

    const getPageTagIcons = () => {
        let pageIcons: { iconData: Icon; name: string }[] = [];

        function getBlogTopicIcons() {
            return taxonomies?.blogTopics?.map((topic) => {
                if (
                    sortedIcons.byBlogTopic[topic.codename] &&
                    sortedIcons.byBlogTopic[topic.codename][iconColor]
                ) {
                    let iconData =
                        sortedIcons.byBlogTopic[topic.codename][iconColor][0];
                    return {
                        iconData: iconData,
                        name: iconData.blog_topic.value[0]?.name,
                    };
                }
            });
        }

        function getWellnessTypeIcons() {
            return taxonomies?.wellnessTypes?.map((topic) => {
                if (
                    sortedIcons.byWellnessType[topic.codename] &&
                    sortedIcons.byWellnessType[topic.codename][iconColor]
                ) {
                    let iconData =
                        sortedIcons.byWellnessType[topic.codename][
                            iconColor
                        ][0];
                    return {
                        iconData: iconData,
                        name: iconData.wellness_type.value[0]?.name,
                    };
                }
            });
        }

        switch (relevantPageTaxonomy) {
            case 'blog_topic':
                pageIcons = getBlogTopicIcons();
                break;
            case 'wellness_type':
                pageIcons = getWellnessTypeIcons();
                break;
            default:
                let blogTopicIcons = getBlogTopicIcons();
                let wellnessTypeIcons = getWellnessTypeIcons();
                pageIcons = [...blogTopicIcons, ...wellnessTypeIcons];
        }

        return pageIcons;
    };

    const pageTagIcons = getPageTagIcons();

    const [currentTag, setCurrentTag] = useState(0);

    const handleClickPrev = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        setCurrentTag(currentTag - 1);
    };

    const handleClickNext = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        setCurrentTag(currentTag + 1);
    };

    const renderIcon = (
        icon: { iconData: Icon; name: string },
        idx: number
    ) => {
        if (breakpoints.md || idx === currentTag) {
            const iconSvgData = icon?.iconData?.icon_svg_file?.value[0];

            return (
                <LazyMotion features={domAnimation} key={idx}>
                    <AnimatePresence>
                        <m.div
                            key={idx}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={styles.tag}
                        >
                            <div className={styles.iconWrapper}>
                                <object
                                    type="image/svg+xml"
                                    data={iconSvgData?.url}
                                >
                                    <img
                                        src={iconSvgData?.url}
                                        alt={iconSvgData?.description}
                                    />
                                </object>
                                {!breakpoints.md && currentTag != 0 ? (
                                    <ArrowLgButton
                                        direction="left"
                                        handleClick={handleClickPrev}
                                        strokeColor={arrowStrokeColor}
                                        className={styles.prev}
                                    />
                                ) : null}
                                {!breakpoints.md &&
                                currentTag != pageTagIcons.length - 1 ? (
                                    <ArrowLgButton
                                        direction="right"
                                        handleClick={handleClickNext}
                                        strokeColor={arrowStrokeColor}
                                        className={styles.next}
                                    />
                                ) : null}
                            </div>
                            {icon?.name}
                        </m.div>
                    </AnimatePresence>
                </LazyMotion>
            );
        }
    };

    return (
        <div className={styles.wrapper} data-kontent-item-id={kontentItemId}>
            <div className={styles.content}>
                <h2 data-kontent-element-codename="statement">
                    {statement?.value}
                </h2>
                <div className={styles.pageTags}>
                    {pageTagIcons?.map((icon, idx) => {
                        return renderIcon(icon, idx);
                    })}
                </div>
            </div>
        </div>
    );
};

export default PageRelevancyTags;
